import React, { useEffect, useRef, useState } from "react";
import { Clapperboard } from "lucide-react";

import ViewContainer from "../../../../../components/layout/view/ViewContainer";

import ContentPanel from "../../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../../components/layout/content/ContentPanelHeader";

import EmptyState from "../../../../../components/feedback/EmptyState";
import RefreshButton from "../../../../../components/buttons/refresh/RefreshButton";
import Button from "../../../../../components/buttons/Button";
import ContentGrid from "../../../../../components/grids/content/ContentGrid";
import PopThumbnailCard from "../../../../../components/cards/pops/PopThumbnailCard";

import { useUserGroupPops } from "../hooks/useGroupMemberPops";
import { useUserPopsViews } from "../../../saved/users/hooks/useUserPopsViews";

import GroupMemberPopsFeed from "./GroupMemberPopsFeed";

import "./GroupMemberPopsView.css";

const DEFAULT_LIMIT = 12;
const PREVIEW_LIMIT = 5;
const VIEW_BATCH_SIZE = 50;

const GRID_CONFIG = {
  columns: 6,
  tabletColumns: 3,
  mobileColumns: 2,
  gap: 16,
  showEmpty: false,
};

const getErrorMessage = (error) =>
  typeof error === "string"
    ? error
    : error?.message || "Unable to load group Pops.";

export default function GroupMemberPopsView({
  groupTag,
  limit = DEFAULT_LIMIT,
  preview = false,
  onContentLoaded,
  onPreviewResult,
}) {
  const pageLimit = preview
    ? PREVIEW_LIMIT
    : Math.max(1, Number(limit) || DEFAULT_LIMIT);

  const {
    popThumbs,
    loading,
    loadingMore,
    error,
    hasMore,
    getUserGroupPopThumbs,
    loadMoreUserGroupPops,
  } = useUserGroupPops(groupTag, pageLimit);

  const { getPopViewCounts, getPopViewCount } = useUserPopsViews();

  const [selectedPopTag, setSelectedPopTag] = useState(null);

  const loadedIdsRef = useRef(new Set());
  const pendingIdsRef = useRef(new Set());
  const versionRef = useRef(0);
  const popsRef = useRef([]);

  const popList = Array.isArray(popThumbs)
    ? popThumbs.filter((pop) => pop?.popTag)
    : [];

  const pops = preview ? popList.slice(0, PREVIEW_LIMIT) : popList;

  const errorMessage = error ? getErrorMessage(error) : "";
  const isEmpty = !loading && pops.length === 0;

  popsRef.current = pops;

  useEffect(() => {
    versionRef.current += 1;
    loadedIdsRef.current.clear();
    pendingIdsRef.current.clear();
    setSelectedPopTag(null);

    return () => {
      versionRef.current += 1;
    };
  }, [groupTag, pageLimit]);

  useEffect(() => {
    if (!preview || loading) return;

    const notify = onContentLoaded || onPreviewResult;

    notify?.({
      hasContent: pops.length > 0,
      error: Boolean(errorMessage),
    });
  }, [
    preview,
    loading,
    pops.length,
    errorMessage,
    onContentLoaded,
    onPreviewResult,
  ]);

  useEffect(() => {
    if (!groupTag || !pops.length) return;

    const version = versionRef.current;

    const ids = [
      ...new Set(
        pops
          .map((pop) => Number(pop.popId))
          .filter((id) => Number.isSafeInteger(id) && id > 0),
      ),
    ];

    const missingIds = ids.filter(
      (id) => !loadedIdsRef.current.has(id) && !pendingIdsRef.current.has(id),
    );

    for (let i = 0; i < missingIds.length; i += VIEW_BATCH_SIZE) {
      const batch = missingIds.slice(i, i + VIEW_BATCH_SIZE);

      batch.forEach((id) => pendingIdsRef.current.add(id));

      Promise.resolve()
        .then(() => getPopViewCounts(batch))
        .then((results) => {
          if (version !== versionRef.current) return;

          if (Array.isArray(results)) {
            results.forEach((result) => {
              const id = Number(result?.popId);

              if (Number.isSafeInteger(id) && id > 0) {
                loadedIdsRef.current.add(id);
              }
            });
          }
        })
        .catch((requestError) => {
          console.error("Unable to load group Pop views:", requestError);
        })
        .finally(() => {
          if (version !== versionRef.current) return;

          batch.forEach((id) => pendingIdsRef.current.delete(id));
        });
    }
  }, [groupTag, pageLimit, popThumbs]);

  const refreshViews = async () => {
    const ids = [
      ...new Set(
        popsRef.current
          .map((pop) => Number(pop.popId))
          .filter((id) => Number.isSafeInteger(id) && id > 0),
      ),
    ];

    for (let i = 0; i < ids.length; i += VIEW_BATCH_SIZE) {
      try {
        await getPopViewCounts(ids.slice(i, i + VIEW_BATCH_SIZE));
      } catch (requestError) {
        console.error("Unable to refresh group Pop views:", requestError);
      }
    }
  };

  const handleRefresh = async () => {
    if (!groupTag || loading || loadingMore) return;

    versionRef.current += 1;
    loadedIdsRef.current.clear();
    pendingIdsRef.current.clear();

    await getUserGroupPopThumbs();
  };

  const handleLoadMore = async () => {
    if (preview || loading || loadingMore || !hasMore) return;

    await loadMoreUserGroupPops();
  };

  const handleOpenPop = (pop) => {
    if (!pop?.popTag) return;

    setSelectedPopTag(String(pop.popTag));
  };

  const handleClosePop = () => {
    setSelectedPopTag(null);
    void refreshViews();
  };

  const renderPop = (pop) => (
    <PopThumbnailCard
      key={pop.popTag}
      pop={{
        ...pop,
        views: getPopViewCount(pop.popId, 0),
      }}
      mode="grid"
      onClick={handleOpenPop}
    />
  );

  if (preview && isEmpty && !errorMessage) {
    return null;
  }

  return (
    <>
      <ViewContainer>
        <ContentPanel>
          <ContentPanelHeader
            title={`Group Pops (${pops.length})`}
            subTitle="Pops shared with this group."
          >
            {!preview && (
              <RefreshButton
                label="Refresh"
                loading={loading}
                disabled={loading || loadingMore}
                onClick={handleRefresh}
              />
            )}
          </ContentPanelHeader>

          {errorMessage && pops.length > 0 && (
            <div className="group-member-pops__alert" role="alert">
              {errorMessage}
            </div>
          )}

          {loading && !pops.length ? (
            <ContentGrid
              {...GRID_CONFIG}
              items={[]}
              loading
              skeletonCount={preview ? PREVIEW_LIMIT : pageLimit}
              renderItem={() => null}
            />
          ) : !pops.length ? (
            !preview && (
              <EmptyState
                icon={<Clapperboard size={28} />}
                title={
                  errorMessage ? "Unable to load Pops" : "No Pops available"
                }
                description={
                  errorMessage ||
                  "Pops shared with this group will appear here."
                }
              />
            )
          ) : (
            <ContentGrid {...GRID_CONFIG} items={pops} renderItem={renderPop} />
          )}

          {!preview && hasMore && pops.length > 0 && (
            <div className="group-member-pops__footer">
              <Button
                value={loadingMore ? "Loading..." : "Load More"}
                variant="primary-outlined"
                disabled={loading || loadingMore}
                onClick={handleLoadMore}
              />
            </div>
          )}
        </ContentPanel>
      </ViewContainer>

      {selectedPopTag && (
        <GroupMemberPopsFeed
          key={`${groupTag}-${selectedPopTag}`}
          groupTag={groupTag}
          initialPopTag={selectedPopTag}
          onClose={handleClosePop}
          showCloseButton
        />
      )}
    </>
  );
}
